import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Brain,
  Database,
  Shield,
  Network,
  Zap,
  Users,
  BookOpen,
  MessageSquare,
  ArrowRight,
  CheckCircle,
  Globe,
  Lock,
  Cpu,
  TrendingUp,
} from 'lucide-react';

export default function LearnMorePage() {
  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20 py-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <Badge className="mb-6 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20">
              Powered by OriginTrail DKG
            </Badge>
            <h1 className="mb-6 text-5xl font-bold tracking-tight">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                TalkTrail
              </span>
            </h1>
            <p className="mb-8 text-xl text-zinc-400 leading-relaxed">
              Where knowledge meets AI memory. TalkTrail is a revolutionary
              platform that combines blogging and forum discussions, storing all
              data on a decentralized knowledge graph to power the next
              generation of AI agents.
            </p>
          </div>
        </div>
      </section>

      {/* What is TalkTrail */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4 text-white">
                What is TalkTrail?
              </h2>
              <p className="text-lg text-zinc-400 max-w-3xl mx-auto">
                TalkTrail is more than just a content platform—it's a knowledge
                infrastructure that bridges human creativity with AI
                intelligence through decentralized technology.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <BookOpen className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-white">
                      Blog Platform
                    </h3>
                    <p className="text-zinc-400">
                      Create and share insightful articles with our community.
                      Every piece of content becomes part of a larger knowledge
                      network.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-pink-500/10 border border-pink-500/20">
                    <MessageSquare className="h-6 w-6 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-white">
                      Forum Discussions
                    </h3>
                    <p className="text-zinc-400">
                      Engage in meaningful conversations and debates. Connect
                      with like-minded individuals and expand your perspectives.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <Brain className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-white">
                      AI Memory Layer
                    </h3>
                    <p className="text-zinc-400">
                      Your content and discussions become part of an AI agent's
                      memory, creating a persistent knowledge base for future
                      interactions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-8 border border-white/10">
                  <div className="h-full w-full rounded-xl bg-black/50 backdrop-blur-sm shadow-lg flex items-center justify-center border border-white/5">
                    <div className="text-center space-y-4">
                      <div className="flex justify-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-purple-400 animate-pulse" />
                        <div className="h-3 w-3 rounded-full bg-pink-400 animate-pulse delay-75" />
                        <div className="h-3 w-3 rounded-full bg-blue-400 animate-pulse delay-150" />
                      </div>
                      <p className="text-sm text-zinc-400">
                        Knowledge flowing through the DKG
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OriginTrail DKG Section */}
      <section className="py-20 bg-zinc-900/30">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-green-500/10 text-green-400 border border-green-500/20">
                Powered by OriginTrail
              </Badge>
              <h2 className="text-3xl font-bold mb-4 text-white">
                Built on the Decentralized Knowledge Graph
              </h2>
              <p className="text-lg text-zinc-400 max-w-3xl mx-auto">
                TalkTrail leverages OriginTrail's revolutionary DKG technology
                to create a verifiable, decentralized knowledge layer that
                powers trusted AI applications.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="border border-green-500/20 bg-black/40">
                <CardHeader>
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10 mb-4 border border-green-500/20">
                    <Database className="h-6 w-6 text-green-400" />
                  </div>
                  <CardTitle className="text-white">Knowledge Assets</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-400">
                    Every article and discussion becomes a Knowledge Asset—an
                    ownable, verifiable piece of knowledge with cryptographic
                    proofs and semantic connections.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-blue-500/20 bg-black/40">
                <CardHeader>
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10 mb-4 border border-blue-500/20">
                    <Shield className="h-6 w-6 text-blue-400" />
                  </div>
                  <CardTitle className="text-white">
                    Verifiable Provenance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-400">
                    Blockchain-anchored proofs ensure that every piece of
                    content can be traced back to its original creator,
                    maintaining trust and authenticity.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-purple-500/20 bg-black/40">
                <CardHeader>
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500/10 mb-4 border border-purple-500/20">
                    <Network className="h-6 w-6 text-purple-400" />
                  </div>
                  <CardTitle className="text-white">
                    Semantic Connections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-400">
                    Content is interconnected through semantic relationships,
                    creating a rich web of knowledge that AI agents can navigate
                    and understand.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* DKG Architecture Visualization */}
            <Card className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/20">
              <CardHeader>
                <CardTitle className="text-center text-white">
                  Three-Layer Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center space-y-3">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-purple-500/10 border border-purple-500/20">
                      <Cpu className="h-8 w-8 text-purple-400" />
                    </div>
                    <h3 className="font-semibold text-white">
                      Verifiable AI Layer
                    </h3>
                    <p className="text-sm text-zinc-400">
                      AI agents and applications that leverage the knowledge
                      base for trusted, contextual interactions.
                    </p>
                  </div>
                  <div className="text-center space-y-3">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/10 border border-blue-500/20">
                      <Database className="h-8 w-8 text-blue-400" />
                    </div>
                    <h3 className="font-semibold text-white">
                      Knowledge Base Layer
                    </h3>
                    <p className="text-sm text-zinc-400">
                      Decentralized network of nodes storing and serving
                      knowledge assets in semantic format.
                    </p>
                  </div>
                  <div className="text-center space-y-3">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 border border-green-500/20">
                      <Shield className="h-8 w-8 text-green-400" />
                    </div>
                    <h3 className="font-semibold text-white">Trust Layer</h3>
                    <p className="text-sm text-zinc-400">
                      Blockchain networks providing consensus, identity, and
                      cryptographic verification.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Agentic Memory Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-orange-500/10 text-orange-400 border border-orange-500/20">
                  Future of AI
                </Badge>
                <h2 className="text-3xl font-bold mb-6 text-white">
                  Powering Agentic Memory
                </h2>
                <p className="text-lg text-zinc-400 mb-6">
                  TalkTrail's content doesn't just live in isolation—it becomes
                  part of a collective memory that AI agents can access, learn
                  from, and build upon.
                </p>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-zinc-300">
                      Persistent memory across AI interactions
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-zinc-300">
                      Contextual understanding of discussions
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-zinc-300">
                      Verifiable knowledge for trusted AI responses
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-zinc-300">
                      Collective intelligence from community contributions
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <Card className="border-l-4 border-l-purple-500 bg-zinc-900/50 border-y border-r border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Brain className="h-8 w-8 text-purple-400 mt-1" />
                      <div>
                        <h3 className="font-semibold mb-2 text-white">
                          Neural-Symbolic AI
                        </h3>
                        <p className="text-sm text-zinc-400">
                          Combines the pattern recognition of neural networks
                          with the structured reasoning of knowledge graphs.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-blue-500 bg-zinc-900/50 border-y border-r border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Zap className="h-8 w-8 text-blue-400 mt-1" />
                      <div>
                        <h3 className="font-semibold mb-2 text-white">
                          Decentralized RAG
                        </h3>
                        <p className="text-sm text-zinc-400">
                          Retrieval-Augmented Generation powered by verifiable,
                          decentralized knowledge sources.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500 bg-zinc-900/50 border-y border-r border-white/10">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Globe className="h-8 w-8 text-green-400 mt-1" />
                      <div>
                        <h3 className="font-semibold mb-2 text-white">
                          Paranet Integration
                        </h3>
                        <p className="text-sm text-zinc-400">
                          TalkTrail operates as a paranet, contributing to the
                          global knowledge graph ecosystem.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-zinc-900/30">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4 text-white">
                Why Choose TalkTrail?
              </h2>
              <p className="text-lg text-zinc-400">
                Experience the future of knowledge sharing and AI interaction
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="text-center bg-black/40 border border-white/10">
                <CardContent className="p-6">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500/10 mb-4 border border-purple-500/20">
                    <Lock className="h-6 w-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold mb-2 text-white">
                    Own Your Content
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Your articles and posts become Knowledge Assets that you
                    truly own
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center bg-black/40 border border-white/10">
                <CardContent className="p-6">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10 mb-4 border border-blue-500/20">
                    <Shield className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="font-semibold mb-2 text-white">
                    Verifiable Truth
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Cryptographic proofs ensure content authenticity and
                    provenance
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center bg-black/40 border border-white/10">
                <CardContent className="p-6">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10 mb-4 border border-green-500/20">
                    <Users className="h-6 w-6 text-green-400" />
                  </div>
                  <h3 className="font-semibold mb-2 text-white">
                    Community Driven
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Decentralized governance and community-owned knowledge
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center bg-black/40 border border-white/10">
                <CardContent className="p-6">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-4 border border-orange-500/20">
                    <TrendingUp className="h-6 w-6 text-orange-400" />
                  </div>
                  <h3 className="font-semibold mb-2 text-white">
                    Future Ready
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Built for the next generation of AI and Web3 applications
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
